import { ClickEvent, MapPin, MapVendor, RouteType, DistanceProfileService } from "@mcleod/components";
import { AutogenLayoutMovementMap } from "./autogen/AutogenLayoutMovementMap";
import { Api, getThemeColor, MapSettings } from "@mcleod/core";
import { LMEUserSettings } from "@mcleod/general";
import { DesignableObjectLogManager } from "@mcleod/components/src/logging/DesignableObjectLogManager";

const DELIVERED_STATUS_CODE = "D";
const PCMILER_ON_PREM = 'A';
const PCMILER_WEB = 'W';       // Used in MovementMap.ts
const PCMILER_WEB_ALT = 'T';   // Used in DistanceRateIndexCalculator.ts
const MILEMAKER = 'E';

const getDefaultRouteConfig = () => ({
    showGoogleRoute: false,
    showGoogleToggle: false,
    showMileMakerRoute: LMEUserSettings.get()?.show_proposed_route === "Y",
    showMileMakerToggle: true,
    showTrimbleRoute: LMEUserSettings.get()?.show_proposed_route === "Y",
    showTrimbleToggle: true
});

const log = DesignableObjectLogManager.getLogger("components.map");

export class MovementMap extends AutogenLayoutMovementMap {
    private movementMapVendor: string = MapSettings.getSingleton().getMovementMapVendor();
    private googleRouteDrawn: boolean = false;
    private isPcMilerOnPremVendor: boolean = false;
    private isMileMakerVendor: boolean = false;
    private _googleShowRoute = false;
    private labelMapinfo: any = { text: "", visible: false };
    private readonly GoogleMapVendor = 'G';
    private readonly TrimbleMapVendor = 'W';
    private readonly MULTI_VERSION_PCMILER = 'M';
    private previousIndex: number | null = null;
    private distanceVendorInfo: any = null;
    private isPcMilerWebVendor: boolean = false;
    private routeConfig = this.getConfigWithUserSettings();
    public mapPinOnClick: (event: ClickEvent, pin: MapPin) => void;

    onLoad(): void | Promise<void> {
        this.addMapPinFormatting();
        this.addMapDataDisplayListener();
        this.mapStatus.createMapCallback = async () => {
            if (this.sourceMapData.data?.length > 0 && this.shouldDrawRoutes()) {
                await this.createProposedRoute();
            }
        };
        this.fetchDistanceVendorInfo();
    }

    private async fetchDistanceVendorInfo() {
        try {
            this.distanceVendorInfo = await DistanceProfileService.getDistanceVendorInfo("fuel_dist_profile");
            log.debug("MovementMap: Distance vendor info retrieved", this.distanceVendorInfo);
        } catch (error) {
            log.error("Error fetching distance vendor info:", error);
            this.distanceVendorInfo = {
                source: '',
                lookupType: '',
                method: '',
                isMileMaker: false,
                isPcMiler: false
            };
        }
    }

    searchMap(movement_id: string, include_positions: boolean) {
        this.sourceMapData.search({ movement_id, include_positions });
    }

    private getConfigWithUserSettings() {
        // Get base config from MapSettings
        const baseConfig = MapSettings.getSingleton().getRouteConfig();

        // Apply user settings
        const showProposedRoute = LMEUserSettings.get()?.show_proposed_route === "Y";

        return {
            ...baseConfig,
            // Override route visibility with user preference
            showMileMakerRoute: showProposedRoute,
            showTrimbleRoute: showProposedRoute
        };
    }

    configureRoutes(config: Partial<typeof MapSettings.DEFAULT_ROUTE_CONFIG>): void {
        // Get fresh defaults including user settings
        this.routeConfig = {
            ...this.getConfigWithUserSettings(),
            ...config
        };
        log.debug("MovementMap: Route configuration updated", this.routeConfig);
    }

    private addMapDataDisplayListener() {
        this.sourceMapData.addAfterExecutionListener(async (event) => {
            log.debug("MovementMap: Map data loaded", {
                dataLength: this.sourceMapData.data?.length,
                vendor: this.mapStatus.vendor,
                shouldDrawRoutes: this.shouldDrawRoutes()
            });

            // Only create route if we have map data and should draw routes
            if (this.sourceMapData.data?.length > 0 && this.shouldDrawRoutes()) {
                await this.createProposedRoute();
            }
        });
    }

    private shouldDrawRoutes(): boolean {
        const vendor = this.mapStatus.vendor;
        const distanceVendor = MapSettings.getSingleton().getDistanceCalcVendor();

        log.debug("MovementMap: Checking route conditions", {
            vendor,
            distanceVendor,
            PCMILER_ON_PREM,
            PCMILER_WEB,
            MILEMAKER,
            distanceVendorInfo: this.distanceVendorInfo
        });

        if (vendor === MapVendor.GOOGLE) {
            return true;
        } else if (vendor === MapVendor.TRIMBLE) {

            if (this.distanceVendorInfo) {
                const result = this.distanceVendorInfo.isPcMiler;
                log.debug("MovementMap: Using vendor info API to determine route visibility:", result);
                return result;
            } else {
                const result = distanceVendor === PCMILER_ON_PREM || distanceVendor === PCMILER_WEB;
                log.debug("MovementMap: Trimble vendor check result (fallback):", result);
                return result;
            }
        }

        log.debug("MovementMap: No vendor match, returning false");
        return false;
    }

    private async createProposedRoute() {
        try {
            await this.mapStatus.ensureLoaded();

            this.mapStatus.clearRoute();
            this.googleRouteDrawn = false;

            // Set vendor flags up front using distanceVendorInfo
            if (this.distanceVendorInfo) {
                this.isMileMakerVendor = this.distanceVendorInfo.isMileMaker;
                this.isPcMilerWebVendor = false;
                if (this.distanceVendorInfo.isPcMiler) {
                    if (
                        this.distanceVendorInfo.source === PCMILER_WEB ||
                        this.distanceVendorInfo.source === PCMILER_WEB_ALT
                    ) {
                        this.isPcMilerWebVendor = true;
                    }
                    if (this.distanceVendorInfo.source === PCMILER_ON_PREM) {
                        this.isPcMilerOnPremVendor = true;
                    }
                } else {
                    this.isPcMilerOnPremVendor = false;
                }
            } else {
                this.isPcMilerOnPremVendor = false;
                this.isMileMakerVendor = false;
                this.isPcMilerWebVendor = false;
            }

            // Build data from stops (exclude positions and callins)
            const stopData = this.sourceMapData.data
                .filter(row => row.get("data_type") === "stop")
                .sort((a, b) => a.get("movement_sequence") - b.get("movement_sequence"))
                .map(row => ({
                    order_sequence: row.get("order_sequence"),
                    latitude: row.get("latitude"),
                    longitude: row.get("longitude"),
                    city_id: row.get("city_id"),
                    city_name: row.get("city_name"),
                    state: row.get("state"),
                    zip_code: row.get("zip_code"),
                }));

            log.debug("MovementMap: Route data built", {
                totalRows: this.sourceMapData.data?.length,
                stopRows: this.sourceMapData.data?.filter(row => row.get("data_type") === "stop").length,
                stops: stopData
            });

            if (stopData.length < 2) {
                log.debug("MovementMap: Not enough stops for route", stopData.length);
                return; // Need at least 2 stops to create a route
            }

            const showGoogleRoute = this.routeConfig.showGoogleRoute;
            log.debug("MovementMap: Google route visibility setting", showGoogleRoute);

            const showProposedRoute = this.routeConfig.showMileMakerRoute;
            if (this.movementMapVendor === this.GoogleMapVendor && showProposedRoute) {
                const response = await Api.search("lme/dispatch/distance-lookup", {
                    stops: stopData,
                    dist_profile: "fuel_dist_profile" // Hardcoded for now, can be parameterized later
                });

                log.debug("MovementMap: Populate map with route data", {
                    response,
                    routeDataLength: stopData.length,
                    stops: stopData
                });
                this.populateMap(response, stopData);
            } else if (this.movementMapVendor === this.TrimbleMapVendor && (this.isPcMilerOnPremVendor || this.isPcMilerWebVendor)) {
                try {
                    // Get route data from API
                    const response = await Api.search("lme/dispatch/distance-lookup", {
                        stops: stopData,
                        dist_profile: "fuel_dist_profile"
                    });

                    log.debug("MovementMap: Retrieved Trimble route data", {
                        responseStatus: response?.status,
                        hasTrimbleRoute: Boolean(response?.data[0]?.trimble_route),
                        hasStopCoords: Boolean(response?.data[0]?.route_stop_coords)
                    });

                    if (response?.data?.length > 0) {
                        const routeData = {
                            ...stopData,
                            trimble_coordinate_data: response.data[0].trimble_route || [],
                            route_stop_coords: response.data[0].route_stop_coords || []
                        };

                        const hideMarkers = true;
                        const removeAllPins = false;
                        // Use the route coordinate array if available, otherwise fallback to stop coordinates
                        if (routeData.route_stop_coords?.length > 0) {
                            log.info("MovementMap: Using route coordinates for Trimble route");
                            this.mapStatus.createRouteFromCoordArray(routeData, RouteType.Trimble, hideMarkers, removeAllPins);
                        } else {
                            log.info("MovementMap: No route coordinates available, falling back to stop coordinates");
                            this.mapStatus.createRoute(stopData, RouteType.Trimble, hideMarkers);
                        }
                    } else {
                        log.error("MovementMap: No route data returned from distance lookup");
                        this.mapStatus.createRoute(stopData, RouteType.Trimble);
                    }
                } catch (error) {
                    log.error("Error creating Trimble route:", error);
                    // Fallback to basic route using stop coordinates
                    this.mapStatus.createRoute(stopData, RouteType.Trimble);
                }
            } else {
                // Only plot pins, skip route drawing
                this.populateMap(null, stopData);
            }
        } catch (error) {
            log.error("Error creating proposed route:", error);
        }
    }

    public populateMap(response: any, stopData: any[]) {
        const showGoogleRoute = this.routeConfig.showGoogleRoute;
        const showProposedRoute = this.routeConfig.showMileMakerRoute;
        const showTrimbleRoute = this.routeConfig.showTrimbleRoute;
        const showGoogleToggle = this.routeConfig.showGoogleToggle;
        const showMileMakerToggle = this.routeConfig.showMileMakerToggle;
        const showTrimbleToggle = this.routeConfig.showTrimbleToggle;

        let milemakerRoute = [];
        let trimbleRoute = [];

        // Use route data to determine which vendor routes are available
        if (response && Array.isArray(response.data) && response.data.length > 0) {
            milemakerRoute = response.data[0].milemaker_route || [];
            trimbleRoute = response.data[0].trimble_route || [];
            (trimbleRoute as any).route_stop_coords = response.data[0].route_stop_coords || [];

            // Set vendor flags based on route data presence
            this.isPcMilerOnPremVendor = Array.isArray(trimbleRoute) && trimbleRoute.length > 0;
            this.isMileMakerVendor = Array.isArray(milemakerRoute) && milemakerRoute.length > 0;
            this.isPcMilerWebVendor = false; // Reset the flag

            // If distance vendor info is available, use that as the source of truth
            if (this.distanceVendorInfo) {
                log.debug("MovementMap: Using vendor info API instead of route presence");
                // If no route data is returned but we know MileMaker is the vendor, set the flag anyway
                if (milemakerRoute.length === 0 && this.distanceVendorInfo.isMileMaker) {
                    log.debug("MovementMap: No MileMaker route data but MileMaker is the vendor");
                    this.isMileMakerVendor = true;
                }

                // Check for PC*Miler source type
                if (this.distanceVendorInfo.isPcMiler) {
                    // PC*Miler Web could be identified as either 'W' (constant in this file) or 'T' (from API)
                    if (this.distanceVendorInfo.source === PCMILER_WEB || this.distanceVendorInfo.source === PCMILER_WEB_ALT) {
                        log.debug("MovementMap: PC*Miler Web is the vendor", {
                            source: this.distanceVendorInfo.source,
                            PCMILER_WEB_CONSTANT: PCMILER_WEB,
                            PCMILER_WEB_ALT: PCMILER_WEB_ALT,
                            isPcMilerWeb: true,
                            isMatchingSourceW: this.distanceVendorInfo.source === PCMILER_WEB,
                            isMatchingSourceT: this.distanceVendorInfo.source === PCMILER_WEB_ALT
                        });
                        this.isPcMilerWebVendor = true;
                        // If we're using Google maps with PC*Miler Web, don't show the route
                        if (this.movementMapVendor === this.GoogleMapVendor) {
                            this.isPcMilerOnPremVendor = false;
                        } else {
                            this.isPcMilerOnPremVendor = true;
                        }
                    } else if (trimbleRoute.length === 0) {
                        // For PC*Miler On Prem, if no route data but vendor is PC*Miler, set flag
                        log.debug("MovementMap: No PC*Miler route data but PC*Miler On Prem is the vendor");
                        this.isPcMilerOnPremVendor = true;
                    }
                }
            }
        } else {
            // No response data, use vendor info if available
            if (this.distanceVendorInfo) {
                log.debug("MovementMap: No route data, using vendor info API");
                this.isMileMakerVendor = this.distanceVendorInfo.isMileMaker;
                this.isPcMilerWebVendor = false;

                // Check if we're using PC*Miler Web on a Google map
                if (this.distanceVendorInfo.isPcMiler) {
                    // PC*Miler Web could be identified as either 'W' (constant in this file) or 'T' (from API)
                    if (this.distanceVendorInfo.source === PCMILER_WEB || this.distanceVendorInfo.source === PCMILER_WEB_ALT) {
                        log.debug("MovementMap: PC*Miler Web is the vendor (no route data)", {
                            source: this.distanceVendorInfo.source,
                            PCMILER_WEB_CONSTANT: PCMILER_WEB,
                            PCMILER_WEB_ALT: PCMILER_WEB_ALT,
                            isPcMilerWeb: true,
                            isMatchingSourceW: this.distanceVendorInfo.source === PCMILER_WEB,
                            isMatchingSourceT: this.distanceVendorInfo.source === PCMILER_WEB_ALT
                        });
                        this.isPcMilerWebVendor = true;
                        // If we're using Google maps with PC*Miler Web, don't show the route
                        if (this.movementMapVendor === this.GoogleMapVendor) {
                            this.isPcMilerOnPremVendor = false;
                        } else {
                            this.isPcMilerOnPremVendor = true;
                        }
                    } else {
                        // It's PC*Miler On Prem, allow the route
                        this.isPcMilerOnPremVendor = true;
                    }
                } else {
                    this.isPcMilerOnPremVendor = false;
                }
            } else {
                this.isPcMilerOnPremVendor = false;
                this.isMileMakerVendor = false;
                this.isPcMilerWebVendor = false;
            }
        }

        this.labelMapinfo.visible = true;

        this.updateMap(stopData, showGoogleRoute, trimbleRoute, milemakerRoute, showProposedRoute, showGoogleToggle, showMileMakerToggle, showTrimbleToggle, showTrimbleRoute);
    }

    private updateMap(stopData: any[], showGoogleRoute: boolean, trimbleRoute: any, milemakerRoute: any, showProposedRoute: boolean, showGoogleToggle: boolean, showMileMakerToggle: boolean, showTrimbleToggle: boolean, showTrimbleRoute: boolean) {
        log.debug('MovementMap: updateMap called with isPcMilerWebVendor:', this.isPcMilerWebVendor, 'isPcMilerOnPremVendor:', this.isPcMilerOnPremVendor);

        if (this.movementMapVendor == this.GoogleMapVendor) {
            // For PC*Miler Web vendor on Google Map, explicitly disable Trimble toggle
            const adjustedShowTrimbleToggle = this.isPcMilerWebVendor ? false : showTrimbleToggle;

            const vendorInfo = {
                isMileMaker: this.isMileMakerVendor,
                isPcMiler: this.isPcMilerOnPremVendor,
                isPcMilerWeb: this.isPcMilerWebVendor,
                // Add route toggle configuration
                routeConfig: {
                    showGoogleToggle,
                    showMileMakerToggle,
                    showTrimbleToggle: adjustedShowTrimbleToggle
                }
            };

            log.debug('MovementMap: Route config for map', vendorInfo, 'Original showTrimbleToggle:', showTrimbleToggle, 'Adjusted:', adjustedShowTrimbleToggle);

            // Create route ID for MileMaker
            const milemakerRouteId = JSON.stringify({
                routeId: "milemakerRoute",
                vendorInfo
            });

            // Create route ID for PC*Miler/Trimble
            const trimbleRouteId = JSON.stringify({
                routeId: "trimbleRoute",
                vendorInfo
            });

            const googleRouteId = JSON.stringify({
                routeId: "googleRoute",
                vendorInfo
            });

            this.mapStatus.createRoute(
                stopData,
                RouteType.Google,
                undefined,
                googleRouteId,
                undefined,
                false,
                false
            );

            if (showProposedRoute && milemakerRoute?.length > 0 && this.isMileMakerVendor) {
                // Create MileMaker route with actual route data
                log.debug('MovementMap: Creating MileMaker route with actual data', {
                    milemakerRouteLength: milemakerRoute.length,
                    showProposedRoute
                });

                this.mapStatus.createRoute(
                    milemakerRoute,
                    RouteType.MileMaker,
                    undefined,           // hideMarkers
                    milemakerRouteId,    // routeId with vendor info
                    undefined,           // routeColor
                    showProposedRoute,   // showProposedRoute
                    false                // disableToggleButtons
                );
            } else if (this.isMileMakerVendor && showMileMakerToggle) {
                // Create an "empty" MileMaker route just to pass vendor info
                // This ensures the toggle is created even when the route is hidden
                log.debug('MovementMap: Creating empty MileMaker route to show toggle', {
                    isMileMakerVendor: this.isMileMakerVendor,
                    showMileMakerToggle
                });

                this.mapStatus.createRoute(
                    stopData,                  // Empty route data
                    RouteType.MileMaker,
                    true,                // hideMarkers
                    milemakerRouteId,    // routeId with vendor info
                    undefined,           // routeColor
                    showProposedRoute,   // showProposedRoute (will be false)
                    false                // disableToggleButtons
                );
            }

            // Create PC*Miler/Trimble route if we have data
            log.debug('MovementMap: PC*Miler route creation check', {
                showProposedRoute,
                trimbleRouteLength: trimbleRoute?.length || 0,
                isPcMilerOnPremVendor: this.isPcMilerOnPremVendor,
                isPcMilerWebVendor: this.isPcMilerWebVendor,
                showTrimbleRoute,
                shouldCreateRoute: showProposedRoute &&
                                  trimbleRoute?.length > 0 &&
                                  this.isPcMilerOnPremVendor &&
                                  !this.isPcMilerWebVendor &&
                                  showTrimbleRoute,
                distanceVendorInfo: this.distanceVendorInfo
            });

            if (showProposedRoute && trimbleRoute?.length > 0 &&
                this.isPcMilerOnPremVendor && !this.isPcMilerWebVendor &&
                showTrimbleRoute) {
                // Create PC*Miler route with actual route data
                log.debug('MovementMap: Creating PC*Miler route with actual data', {
                    trimbleRouteLength: trimbleRoute.length,
                    showProposedRoute,
                    showTrimbleRoute,
                    isPcMilerWebVendor: this.isPcMilerWebVendor,
                    movementMapVendor: this.movementMapVendor
                });

                this.mapStatus.createRoute(
                    trimbleRoute,
                    RouteType.Trimble,
                    undefined,           // hideMarkers
                    trimbleRouteId,      // routeId with vendor info
                    undefined,           // routeColor
                    showProposedRoute,   // showProposedRoute
                    false                // disableToggleButtons
                );
            } else if (this.isPcMilerOnPremVendor && !this.isPcMilerWebVendor && showTrimbleToggle) {
                // Create an "empty" PC*Miler route just to pass vendor info
                // This ensures the toggle is created even when the route is hidden
                log.debug('MovementMap: Creating empty PC*Miler route to show toggle', {
                    isPcMilerOnPremVendor: this.isPcMilerOnPremVendor,
                    isPcMilerWebVendor: this.isPcMilerWebVendor,
                    showTrimbleToggle,
                    distanceVendorInfo: this.distanceVendorInfo
                });

                this.mapStatus.createRoute(
                    stopData,           // Empty route data
                    RouteType.Trimble,
                    true,                // hideMarkers
                    trimbleRouteId,      // routeId with vendor info
                    undefined,           // routeColor
                    showProposedRoute,   // showProposedRoute (will be false)
                    false                // disableToggleButtons
                );
            }
        }
    }

    changeMapPinFocus(mapPinIndex: number) {
        if(this.previousIndex !== null && this.previousIndex !== mapPinIndex) {
            const previousMapPin = {...this.mapStatus.pins[this.previousIndex]};
            this.mapStatus.pins[this.previousIndex] = previousMapPin;
            this.mapStatus.defocusOnPin(previousMapPin, this.previousIndex);
        }

        this.previousIndex = mapPinIndex;

        const pinToUpdate : MapPin = this.mapStatus.pins[mapPinIndex];
        this.mapStatus.focusOnPin(pinToUpdate, mapPinIndex);
    }

    private addMapPinFormatting() {
        this.mapStatus.addPinPlotListener(event => {
            const pin: MapPin = event.pin;
            const dataType = pin.data.get("data_type");
            const callinId = pin.data?.get("callin_id");
            if (dataType === "stop") {
                pin.caption = "" + pin.data.get("movement_sequence");
                pin.clusterable = false;
                if (pin.data?.get("status") === DELIVERED_STATUS_CODE) {
                    pin.image = "map-pin-filled";
                    pin.captionColor = getThemeColor("primary.reverse");
                    this.createToolTipsForStops(pin);
                }
                else {
                    pin.image = "map-pin-outlined";
                    pin.captionColor = getThemeColor("primary");
                    this.createToolTipsForStops(pin);
                }
            } else if (dataType === "callin" || callinId != null) {
                pin.image = pin.data?.get("image") ?? "map-pin-white-dot-small";
                if (pin.data?.get("descr") != null) {
                    pin.tooltip = pin.data.get("descr");
                }

                pin.onClick = (e: ClickEvent) => {
                    this.mapPinOnClick?.(e, pin);
                }
            } else if (dataType == "tractor_position") {
                pin.image = pin.data.get("image");
                this.createTooltipForPosition(pin);
                pin.onClick = (e: ClickEvent) => {
                    this.mapPinOnClick?.(e, pin);
                }
            }
        });
    }

    private createToolTipsForStops(pin: MapPin) {
        const stopType = pin.data?.get("stop_type_value") + " - ";
        const stopStatus = "Status: " + pin.data?.get("stop_status_info") + "\n";
        const stopLocationInfo = "Stop Name: " + pin.data?.get("stop_location") + "\n";
        const stopDateInfo = "Date/Time: " + pin.data?.get("stop_date_info");
        pin.tooltip = `${stopType}${stopStatus}${stopLocationInfo}${stopDateInfo}`;
    }

    private createTooltipForPosition(pin: MapPin) {
        const position = `Position: ${pin.data?.get("position")}`;
        const date = `Date/Time: ${pin.data?.get("position_date")}`;
        pin.tooltip = `${position}\n${date}`;
    }

}
